/**
 * Hook del centro de notificaciones in-app (la campana del Topbar).
 *
 * Lee la tabla `notifications` (RLS: el usuario ve las de su tenant + las dirigidas a
 * él o tenant-wide), calcula el contador de no leídas, y se suscribe al broadcast
 * realtime que emite el adapter in-app (`notifications-${tenantId}`, event 'notification')
 * para que el badge se actualice al instante sin recargar.
 *
 * Es la pieza F2 que faltaba: el backend ya persistía + difundía, pero nadie lo leía
 * (la campana tenía un contador hardcodeado). Acá también entran las alertas proactivas
 * del copiloto (type 'copilot.alert').
 *
 * ⭐ La lista vive en la caché (ADR-0012, paso 3), no en un `useState`. La campana la monta el
 * **Topbar**: antes se volvía a pedir en cada navegación del panel. Ahora se pide una vez y las
 * demás pantallas la leen de memoria.
 *
 * 🪤 Con caché, el realtime y las marcas de leído tienen que ESCRIBIR en la caché
 * (`setQueryData`), no en un estado local. Un `useState` paralelo se pisaría con la revalidación:
 * el badge volvería a subir solo unos segundos después de que el usuario lo bajó.
 */

import { useCallback, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@smarty/shared';
import { supabase } from '@/lib/supabase';

export interface AppNotification {
  id: string;
  type: string;
  title: string;
  body: string;
  url: string | null;
  data: Record<string, unknown>;
  read_at: string | null;
  created_at: string;
}

export interface NotificationsState {
  notifications: AppNotification[];
  unreadCount: number;
  loading: boolean;
  markRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
  refresh: () => Promise<void>;
}

const LIMIT = 20;
const VACIO: AppNotification[] = [];

export function useNotifications(): NotificationsState {
  const { tenantId } = useAuth();
  const qc = useQueryClient();
  const habilitado = !!tenantId;
  // Estable entre renders: es la clave que comparten la consulta, el realtime y las marcas de
  // leído. Escribir una clave distinta en cualquiera de los tres los desconectaría en silencio.
  const clave = ['notifications', tenantId] as const;

  const query = useQuery({
    queryKey: clave,
    enabled: habilitado,
    // Corto: son avisos, y llegar tarde a uno es peor que una consulta de más. El realtime cubre
    // lo instantáneo; esto cubre el caso de haber estado con la pestaña cerrada.
    staleTime: 30_000,
    queryFn: async (): Promise<AppNotification[]> => {
      const { data, error } = await supabase
        .from('notifications')
        .select('id, type, title, body, url, data, read_at, created_at')
        .eq('tenant_id', tenantId!)
        .order('created_at', { ascending: false })
        .limit(LIMIT);
      if (error) throw new Error(error.message);
      return (data ?? []) as AppNotification[];
    },
  });

  // Realtime: el adapter in-app difunde al topic notifications-${tenantId}. Insertamos
  // la nueva notificación al toque (badge instantáneo) sin round-trip; dedup por id.
  useEffect(() => {
    if (!tenantId) return;
    const channel = supabase
      .channel(`notifications-${tenantId}`)
      .on('broadcast', { event: 'notification' }, (msg: { payload?: Partial<AppNotification> }) => {
        const p = msg.payload;
        if (!p?.id || !p.title) return;
        qc.setQueryData<AppNotification[]>(clave, (prev = []) => {
          if (prev.some((n) => n.id === p.id)) return prev;
          const entrante: AppNotification = {
            id: p.id, type: p.type ?? '', title: p.title ?? '', body: p.body ?? '',
            url: p.url ?? null, data: (p.data as Record<string, unknown>) ?? {},
            read_at: null, created_at: new Date().toISOString(),
          };
          return [entrante, ...prev].slice(0, LIMIT);
        });
      })
      .subscribe();
    return () => { void supabase.removeChannel(channel); };
    // `clave` es un array nuevo en cada render; se depende de `tenantId`, que es lo que la define.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, qc]);

  const markRead = useCallback(async (id: string) => {
    const ahora = new Date().toISOString();
    qc.setQueryData<AppNotification[]>(['notifications', tenantId], (prev = []) =>
      prev.map((n) => (n.id === id && !n.read_at ? { ...n, read_at: ahora } : n)),
    );
    await supabase.from('notifications').update({ read_at: ahora }).eq('id', id).is('read_at', null);
  }, [qc, tenantId]);

  const markAllRead = useCallback(async () => {
    if (!tenantId) return;
    const ahora = new Date().toISOString();
    qc.setQueryData<AppNotification[]>(['notifications', tenantId], (prev = []) =>
      prev.map((n) => (n.read_at ? n : { ...n, read_at: ahora })),
    );
    await supabase.from('notifications').update({ read_at: ahora }).eq('tenant_id', tenantId).is('read_at', null);
  }, [qc, tenantId]);

  const refresh = useCallback(async () => {
    await query.refetch();
  }, [query]);

  const notifications = query.data ?? VACIO;
  const unreadCount = notifications.reduce((acc, n) => acc + (n.read_at ? 0 : 1), 0);

  // 🪤 `isPending` (no `isFetching`): con datos en pantalla, revalidar no es "cargando". Y la
  // condición de habilitado tiene que entrar, porque con `enabled: false` `isPending` no baja nunca.
  return { notifications, unreadCount, loading: habilitado && query.isPending, markRead, markAllRead, refresh };
}
